import { useEffect, useRef, useState } from 'react';

import type { AccountIndexSchema } from '@liscio/api';
import { useApiClient } from '@liscio/common';

const ACCOUNTS_SEARCH_URL = '/api/v5/firms/accounts';
const EMPTY_SUGGESTIONS: AccountIndexSchema[] = [];
const MIN_SEARCH_LENGTH = 2;

type AccountsSearchResponse = {
  data?: AccountIndexSchema[];
};

export type UseAccountSuggestionsByNameOptions = {
  debounceMs?: number;
  enabled?: boolean;
};

export function isMeaningfulAccountNameQuery(query: string): boolean {
  return query.trim().length >= MIN_SEARCH_LENGTH;
}

export function useAccountSuggestionsByName(
  nameQuery: string,
  options?: UseAccountSuggestionsByNameOptions
) {
  const { client } = useApiClient();
  const requestSequenceRef = useRef(0);
  const [suggestions, setSuggestions] = useState<AccountIndexSchema[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const debounceMs = options?.debounceMs ?? 400;
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    const trimmedQuery = nameQuery.trim();
    const shouldFetch = enabled && isMeaningfulAccountNameQuery(trimmedQuery);

    if (!shouldFetch) {
      requestSequenceRef.current += 1;
      setSuggestions((current) =>
        current.length === 0 ? current : EMPTY_SUGGESTIONS
      );
      setIsLoading(false);
      setError(null);
      return;
    }

    const currentRequestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = currentRequestSequence;
    setIsLoading(true);
    setError(null);

    const timer = window.setTimeout(async () => {
      try {
        const { data } = await client.get<AccountsSearchResponse>(
          ACCOUNTS_SEARCH_URL,
          {
            params: {
              term: trimmedQuery,
              account_name_only: true,
            },
          }
        );

        if (requestSequenceRef.current !== currentRequestSequence) {
          return;
        }

        setSuggestions(data?.data ?? []);
      } catch (requestError) {
        if (requestSequenceRef.current !== currentRequestSequence) {
          return;
        }

        setSuggestions([]);
        setError(requestError);
      } finally {
        if (requestSequenceRef.current === currentRequestSequence) {
          setIsLoading(false);
        }
      }
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [client, debounceMs, enabled, nameQuery]);

  return {
    suggestions,
    isLoading,
    error,
  };
}
